import {SensorType, VehicleModel} from '../../../../../shared/models/vehicle.model';
import {VehiclesService} from '../../../../../data/vehicles/vehicles.service';
import {ToastService} from '../../../../../shared/services/toast.service';
import {Component, OnDestroy, OnInit} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  StatusChangeEvent,
  TouchedChangeEvent,
  ValueChangeEvent
} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {firstValueFrom, mergeMap, mergeWith, of, skipUntil, Subject, Subscription, tap} from 'rxjs';
import {ConfigurationService} from '../../../../../configuration/configuration.service';
import {MatDialog} from '@angular/material/dialog';
import {ConfigurationModel, FeatureFlagEnum} from '../../../../../shared/models/configuration.model';
import {
  CartegraphConfiguration,
  CartegraphFeature,
  CartegraphSettings
} from '../../../../../shared/models/cartegraph.model';
import {RootRoute, SettingsRoute} from '../../../../../shared/models/angular-routing';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {CardComponent} from '../../../../../shared/components/card/card.component';
import {CardContentDirective} from '../../../../../shared/components/card/card-content.directive';
import {RowWithToggleComponent} from '../../row-with-toggle/row-with-toggle.component';
import {
  VehicleBasicInformationEditComponent
} from '../vehicle-basic-information-edit/vehicle-basic-information-edit.component';
import {catchError, debounceTime, filter, map} from 'rxjs/operators';
import {MatError} from '@angular/material/form-field';
import {VehicleControlUnitComponent} from '../plowops-pro/vehicle-hardware/vehicle-control-unit.component';
import {VehicleControlUnit} from '../plowops-pro/model/plowops-pro-models';
import {
  ConfirmationDialogComponent
} from '../../../../../shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import {CartegraphManagementService} from '../../../../../data/cartegraph/cartegraph-management.service';
import {JsonApiResponse} from '../../../../../shared/models/JsonApiResponse';
import {MatButtonToggle, MatButtonToggleGroup} from '@angular/material/button-toggle';

@Component({
  standalone: true,
  selector: 'app-vehicle-detail',
  templateUrl: './vehicle-detail.component.html',
  styleUrls: [
    './vehicle-detail.component.scss',
    '../../../../settings/settings-fields.scss',
    '../../../../settings/settings-common.scss'
  ],
  imports: [
    MatProgressSpinner,
    CardComponent,
    CardContentDirective,
    RowWithToggleComponent,
    ReactiveFormsModule,
    FormsModule,
    VehicleBasicInformationEditComponent,
    MatError,
    VehicleControlUnitComponent,
    MatButtonToggleGroup,
    MatButtonToggle
  ]
})
export class VehicleDetailComponent implements OnInit, OnDestroy {

  vehicle: VehicleModel;
  configuration: ConfigurationModel;
  cartegraphSettings: CartegraphSettings;

  // enums
  FeatureFlagEnum = FeatureFlagEnum;
  SensorType = SensorType;

  manualMaterialLogFeatureEnabled = false;

  isLoading = true;
  isSaving = false;

  vehicleToSave$: Subject<VehicleModel> = new Subject();
  savedVehicle$: Subject<VehicleModel> = new Subject();

  saveError: any;

  formGroup: FormGroup;
  isCartegraphIntegrationEnabled = false;

  private readonly subscriptions: Subscription[] = [];

  constructor(
    private readonly vehicleService: VehiclesService,
    private readonly cartegraphService: CartegraphManagementService,
    private readonly toastService: ToastService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly configurationService: ConfigurationService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    readonly fb: FormBuilder,
  ) {
    this.formGroup = fb.group({
      vehicle: fb.control(null),
      manualMaterialLog: fb.control(null),
      plowOpsPro: fb.control(false),
      vehicleControlUnit: fb.control(null)
    });
  }

  ngOnInit(): void {
    this.loadConfiguration().then(() => {
      this.loadData();
      this.setupFormSaving();
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  loadData(): void {
    this.isLoading = true;

    const vehicleId = +this.activatedRoute.snapshot.paramMap.get('id');
    Promise.all([
      this.cartegraphFeaturesPromise(),
      this.vehicleService.getVehicle(vehicleId)])
      .then(results => {
        const [config, vehicle] = results;
        this.cartegraphSettings = config?.data?.settings;
        this.vehicle = vehicle;
        this.setFormData(this.vehicle);
      }).catch(error => {
      console.error('error occurred', error);
      this.toastService.short('An error occurred to init data');
    }).finally(() => {
      this.isLoading = false;
    });
  }

  hasCartegraphFlag(featureFlag: string): boolean {
    return !!this.cartegraphSettings?.features?.find(value => value.enabled && value.feature === featureFlag);
  }

  navigateBack() {
    const shouldNavigate = this.formGroup.invalid ?
      firstValueFrom(this.dialog.open(ConfirmationDialogComponent, {
        data:
          {
            title: 'Confirm discarding of unsaved changes',
            text: 'The form contains invalid data and cannot be saved. By leaving you\'ll lose any changes. Are you sure?',
            yesLabel: 'Yes, discard the changes'
          }
      }).afterClosed()) : Promise.resolve(true);

    shouldNavigate.then(navigate => {
      if (navigate) {
        this.router.navigate([`/${RootRoute.SETTINGS}`, SettingsRoute.MANAGE_VEHICLES, 'group', this.vehicle.groupId])
          .then();
      }
    });
  }

  onVehicleChanged(vehicle: VehicleModel) {
    this.vehicle = vehicle;
    this.formGroup.markAsUntouched({emitEvent: false});
    this.setFormData(this.vehicle);
  }

  private cartegraphFeaturesPromise(): Promise<JsonApiResponse<CartegraphConfiguration>> {
    if (this.isCartegraphIntegrationEnabled) {
      return this.cartegraphService.getConfiguration();
    }
    return Promise.resolve(null);
  }

  private loadConfiguration() {
    const configurationObservable = this.configurationService.sharedConfigurationModel.pipe(
      filter(model => !!model),
      tap(model => {
        this.configuration = model;
        this.isCartegraphIntegrationEnabled = this.configuration?.hasFeatureFlag(FeatureFlagEnum.CartegraphIntegration);
      })
    );

    this.subscriptions.push(
      configurationObservable.subscribe()
    );
    return firstValueFrom(configurationObservable);
  }

  private setFormData(vehicle: VehicleModel) {
    const {
      id,
      name,
      label,
      publicAlias,
      licensePlate,
      active,
      reportingType,
      mapColor,
      applyColorOverrideOnMobile,
      manualMaterialLog,
      outOfServiceNote,
    } = vehicle;

    const vehicleControlUnit = VehicleControlUnit.fromVehicleModel(vehicle);

    const plowOpsPro = !!vehicleControlUnit;

    this.formGroup.setValue({
      vehicle: {
        id,
        name,
        label,
        publicAlias,
        licensePlate,
        active,
        reportingType,
        mapColor,
        applyColorOverrideOnMobile,
        outOfServiceNote,
      },
      manualMaterialLog: manualMaterialLog ?? null,
      plowOpsPro,
      vehicleControlUnit
    }, {
      emitEvent: false
    });

    this.handlePlowOpsProValueChange(plowOpsPro);

    // cartegraph material logging settings
    this.manualMaterialLogFeatureEnabled = this.isCartegraphIntegrationEnabled
      && this.hasCartegraphFlag(CartegraphFeature.SEND_MATERIAL_LOG)
      && this.hasCartegraphFlag(CartegraphFeature.MANUAL_MATERIAL_LOG);
    if (!this.manualMaterialLogFeatureEnabled) {
      this.formGroup.controls.manualMaterialLog.disable({emitEvent: false});
    }
  }

  private handlePlowOpsProValueChange(plowOpsPro: boolean, userTriggered = false) {
    const vcu = this.formGroup.controls.vehicleControlUnit;
    if (plowOpsPro) {
      if (vcu.disabled) {
        vcu.enable({emitEvent: false});
        if (userTriggered) {
          // store the value of VCU when disabling the PlowOpsPro for recovery purposes
          // by default is empty
          const sessionStoredVcu = sessionStorage.getItem(`plowOpsPro_${this.vehicle.id}`);
          const storedVehicleControlUnit = sessionStoredVcu
            ? Object.assign(new VehicleControlUnit(), JSON.parse(sessionStoredVcu))
            : new VehicleControlUnit();
          vcu.setValue(storedVehicleControlUnit, {emitEvent: false});
        }
      }
    } else if (vcu.enabled) {
      vcu.disable({emitEvent: false});
      if (userTriggered) {
        // remember history value for a session
        sessionStorage.setItem(`plowOpsPro_${this.vehicle.id}`, JSON.stringify(vcu.value));
      }
      // clear the HW values on plowOps pro disable
      vcu.setValue(null, {emitEvent: false});
    }
  }

  private setupFormSaving() {
    this.subscriptions.push(
      this.formGroup.controls.plowOpsPro.valueChanges
        .subscribe(value => this.handlePlowOpsProValueChange(value, true))
    );

    this.subscriptions.push(
      this.formGroup.valueChanges.pipe(
        filter(() => this.formGroup.status === 'VALID'),
        mergeWith(
          this.formGroup.events.pipe(
            filter(e => e instanceof StatusChangeEvent && e.status === 'VALID'),
            map(_ => this.formGroup.value),
          )
        ),
        debounceTime(500),
        skipUntil(this.formGroup.events.pipe(
          filter(e => e instanceof TouchedChangeEvent && e.touched),
        )),
        filter(() => this.formGroup.status === 'VALID'),
      ).subscribe(_ => {
        const value = this.formGroup.value;
        const {vehicle, plowOpsPro, vehicleControlUnit} = value;

        delete value.vehicle;
        delete value.plowOpsPro;
        delete value.vehicleControlUnit;

        const hardware = plowOpsPro ? vehicleControlUnit.toVehicleModel() : {
          lmuId: null,
          hardwareConfiguration: null,
          cameraConfiguration: null,
        };

        this.vehicleToSave$.next({...this.vehicle, ...vehicle, ...value, ...hardware});
      })
    );

    this.subscriptions.push(
      this.formGroup.events.pipe(
        filter(e => e instanceof ValueChangeEvent)
      ).subscribe(() => delete this.saveError)
    );

    this.subscriptions.push(
      this.vehicleToSave$.pipe(
        tap(() => {
          this.isSaving = true;
        }),
        mergeMap(vehicle => {
            return this.vehicleService.updateVehicle(this.vehicle.id, vehicle)
              .pipe(catchError(error => {
                this.saveError = error;
                return of();
              }));
          },
        ),
      ).subscribe(response => {
        this.isSaving = false;
        this.savedVehicle$.next(response.data);
      })
    );
  }
}
